import { FunctionComponent } from "react";
import { Trans, useTranslation } from "react-i18next";

import {
	COMPANY_CONTACT_LOOKUP_CREDITS,
	CreditQuoteAction,
	CreditQuoteResponse,
	PHONE_LOOKUP_CREDITS,
} from "@signalist/shared-types";

import {
	countIncluded,
	CreditConfirmModal,
	formatCredits,
	QUOTE_ACTION_COPY,
	quoteSegmentViews,
} from "@/components/CreditConfirmModal";
import { useGetCreditQuoteQuery } from "@/pages/Private/redux/list/creditQuote.api";

import { BulkContactInfoRequest } from "../../utils/bulkContactInfoRequest";

/** The list lookups the credit-confirm modal quotes from the server. */
export type ListLookupAction = Exclude<CreditQuoteAction, "RESCORE">;

interface LookupCreditConfirmProps {
	action: ListLookupAction;
	/** The request the lookup will send, quoted as it is. */
	request: BulkContactInfoRequest;
	/** The lead's name when the lookup was started from its row. */
	targetName?: string | null;
	/** Leads selected, as the table counts them, until the quote answers. */
	selectedCount: number;
	onClose: () => void;
	/** Runs the lookup; rejects when the API refuses it. */
	onConfirm: () => Promise<unknown>;
}

/** How each lookup charges, in the words of the dialog. */
const chargeNoteOf = (
	action: ListLookupAction,
	t: ReturnType<typeof useTranslation>["t"],
	count: number
): string => {
	if (action === "PHONE") {
		return t("creditConfirm.phone.chargeNote", { unit: formatCredits(PHONE_LOOKUP_CREDITS) });
	}

	if (action === "COMPANY_CONTACT") {
		return t("creditConfirm.companyContact.chargeNote", {
			count: COMPANY_CONTACT_LOOKUP_CREDITS,
		});
	}

	return t(`creditConfirm.${QUOTE_ACTION_COPY[action]}.chargeNote`, { count });
};

/** The lookups the quote holds, when a lead can hold more than one (the email lookup). */
const lookupsOf = (quote: CreditQuoteResponse | undefined): number | undefined =>
	quote?.segments.find((segment) => segment.lookups !== undefined)?.lookups;

/**
 * The confirm of the list's email, phone and company email & phone lookups (PREC-4263): the
 * server quotes the selection the lookup would send, select-all included, and the dialog shows
 * which leads (companies) it searches and which it skips, at the price the API charges.
 */
export const LookupCreditConfirm: FunctionComponent<LookupCreditConfirmProps> = ({
	action,
	request,
	targetName,
	selectedCount,
	onClose,
	onConfirm,
}) => {
	const { t } = useTranslation();
	const quote = useGetCreditQuoteQuery({ ...request, action }, { refetchOnMountOrArgChange: true });
	const segments = quote.data ? quoteSegmentViews(quote.data, t) : undefined;
	const selected = targetName ? 1 : (quote.data?.selected ?? selectedCount);
	const runCount = segments ? countIncluded(segments) : selected;
	const copy = QUOTE_ACTION_COPY[action];
	const target = targetName ?? t("creditConfirm.selectedLeads", { count: selected });

	return (
		<CreditConfirmModal
			isOpened
			body={
				<Trans
					components={{ b: <b className="font-semibold" /> }}
					i18nKey={`creditConfirm.${copy}.body`}
					values={{ target }}
				/>
			}
			chargeNote={chargeNoteOf(action, t, lookupsOf(quote.data) ?? selected)}
			confirmLabel={t(`creditConfirm.${copy}.confirm`, { count: runCount })}
			quoteError={quote.error}
			segments={segments}
			title={t(`creditConfirm.${copy}.title`, { count: selected })}
			onClose={onClose}
			onConfirm={onConfirm}
		/>
	);
};
